import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { AdminUrlBuilder } from '../../../../../../shared/admin/api/admin-url-builder';
import { BreadcrumbBuilder } from '../../../../../../shared/ui/breadcrumb/breadcrumb-builder';
import { IDropdownSearcherOptions } from '../../../../../../shared/ui/component/dropdown-searcher/dropdown-searcher.interfaces';
import { AdminInventoryAddProductModal } from '../../modals/inventory-add-product/inventory-add-product.component';
import { CampaignData } from '../../models/inventory-option.model';
import { AdminInventoryListDatatableComponent } from './datatable/inventory-list-datatable.component';

@Component({
    templateUrl: './inventory-list.component.html',
    styleUrls: ['./inventory-list.component.scss'],
})
export class AdminInventoryListComponent implements OnInit, OnDestroy {
    modalRef: BsModalRef;
    @ViewChild(AdminInventoryListDatatableComponent, { static: false })
    datatableComponent: AdminInventoryListDatatableComponent;

    dropdownSearchOptions: IDropdownSearcherOptions = {
        minSearchPhraseLength: 0,
        dropdownMaxHeight: '420px',
        placeholder: 'Select campaign',
    };

    addProductEventSubscription: Subscription;
    private originalCampaignsOptionsSubject: BehaviorSubject<CampaignData[]> = new BehaviorSubject<CampaignData[]>([
        { value: 0, label: 'All campaigns', campaignUuid: '', languageDefault: 'en' },
    ]);
    campaignsOptionsSubject: BehaviorSubject<CampaignData[]> = new BehaviorSubject<CampaignData[]>([]);
    campaignsOptions$: Observable<CampaignData[]> = this.campaignsOptionsSubject.asObservable();

    selectedCampaign: number = null;
    hasOriginalCampaignOption: boolean = true;

    constructor(
        private modalService: BsModalService,
        private httpClient: HttpClient,
        private urlBuilder: AdminUrlBuilder,
        private breadcrumbBuilder: BreadcrumbBuilder
    ) {}

    ngOnInit(): void {
        this.breadcrumbBuilder.add({
            label: 'Inventory list ',
            url: '/list',
        });

        this.httpClient.get<any>(`${this.buildUrl()}/all`).subscribe((response) => {
            const fetchedCampaigns = response.campaigns.map(
                (campaign: { id: string; name: string; uuid: string; languageDefault: string }) => ({
                    value: campaign.id,
                    label: campaign.name,
                    campaignUuid: campaign.uuid,
                    languageDefault: campaign.languageDefault,
                })
            );
            fetchedCampaigns.sort((a, b) => a.label.localeCompare(b.label));
            this.hasOriginalCampaignOption = fetchedCampaigns.length === 0;

            this.originalCampaignsOptionsSubject.next([
                ...this.originalCampaignsOptionsSubject.getValue(),
                ...fetchedCampaigns,
            ]);

            this.campaignsOptionsSubject.next(this.originalCampaignsOptionsSubject.getValue());
        });
    }

    ngOnDestroy(): void {
        if (this.addProductEventSubscription) this.addProductEventSubscription.unsubscribe();
    }

    private buildUrl() {
        let url = '/campaign';
        if (this.urlBuilder) {
            url = this.urlBuilder.buildUrl(url);
        }

        return url;
    }

    reloadDatatableAfterAdding(): void {
        this.datatableComponent.load();
    }

    handleSearch(phrase: string) {
        const originalOptions = this.originalCampaignsOptionsSubject.getValue();

        if (!phrase) {
            this.campaignsOptionsSubject.next(originalOptions);
            return;
        }

        const filteredOptions = originalOptions.filter((option) => {
            return option.label.toLowerCase().includes(phrase.toLowerCase()) || '';
        });

        this.campaignsOptionsSubject.next(filteredOptions);
    }

    handleChange(selectedItem: CampaignData) {
        this.selectedCampaign = selectedItem.value;
    }

    addProduct() {
        const originalCampaignsOptions = this.originalCampaignsOptionsSubject.getValue();
        const filteredCampaignsOptions = originalCampaignsOptions.slice(1); // remove 'All campaigns'

        this.modalRef = this.modalService.show(AdminInventoryAddProductModal, {
            initialState: {
                campaignsOptions: filteredCampaignsOptions,
                dropdownSearchOptions: this.dropdownSearchOptions,
            },
        });

        this.addProductEventSubscription = this.modalRef.content.onProductAdded.subscribe((result: number) => {
            this.reloadDatatableAfterAdding();
        });
    }
}
